// ONE status per medication (M3 Stage 3 fix 1): the visit, the record, the
// pending queue and the reports all show the badge this function returns, so
// a row can never carry two ("Confirmed — held: not authorized" beside "Held:
// pending authorization" was the Stage 2 bug).
//
// Pure, and free of server-only imports: the SPA imports it too (as
// Account.tsx imports ../server/phone).

export const MED_STATUSES = ["NotReceived", "NotBrought", "AwaitingReview", "HeldPending", "HeldDeclined", "Active", "Discontinued"] as const;
export type MedStatus = (typeof MED_STATUSES)[number];

export const MED_STATUS_LABEL: Record<MedStatus, string> = {
  NotReceived: "Not received",
  NotBrought: "Not brought",
  AwaitingReview: "Received, awaiting Rx review",
  HeldPending: "Held: pending authorization",
  HeldDeclined: "Held: not authorized",
  Active: "Active",
  Discontinued: "Discontinued",
};

/** Tailwind classes for the badge: green for active, amber for waiting, red for held, grey otherwise. */
export const MED_STATUS_TONE: Record<MedStatus, string> = {
  NotReceived: "bg-slate-100 text-slate-800",
  NotBrought: "bg-amber-100 text-amber-950",
  AwaitingReview: "bg-sky-100 text-sky-900",
  HeldPending: "bg-amber-100 text-amber-950",
  HeldDeclined: "bg-red-100 text-red-900",
  Active: "bg-emerald-100 text-emerald-900",
  Discontinued: "bg-slate-200 text-slate-700",
};

/** The columns the status depends on (a MedMedications row's fields, or a view of them). */
export interface MedStatusInput {
  ReceivedStatus?: string;
  AuthorizationStatus?: string;
  Status?: string;
  ConfirmedAtUtc?: string;
}

/**
 * The one status of a medication, from its CURRENT row (or null: a parent's
 * medication with no row yet). Order matters: a medication ended for the
 * session is Discontinued whatever its history; the authorization decides
 * held vs active; a receipt without a review is waiting for the RN.
 */
export function medStatus(f: MedStatusInput | null | undefined): MedStatus {
  if (!f) return "NotReceived";
  if (f.Status === "Discontinued" || f.Status === "Departed") return "Discontinued";
  if (f.ReceivedStatus === "NotBrought") return "NotBrought";
  if (f.AuthorizationStatus === "Declined") return "HeldDeclined";
  if (f.AuthorizationStatus === "Pending" || f.AuthorizationStatus === "Held") return "HeldPending";
  if (f.Status === "Active" && (f.AuthorizationStatus === "OnFile" || f.AuthorizationStatus === "Authorized")) return "Active";
  if (!f.ReceivedStatus && !f.ConfirmedAtUtc) return "NotReceived";
  return "AwaitingReview";
}

export function medStatusLabel(f: MedStatusInput | null | undefined): string {
  return MED_STATUS_LABEL[medStatus(f)];
}

/** The same status from the camelCase view the screens receive (station MedView and friends). */
export function medStatusOfView(v: { receivedStatus?: string; authorizationStatus?: string; status?: string; confirmedAtUtc?: string } | null | undefined): MedStatus {
  return medStatus(v ? { ReceivedStatus: v.receivedStatus, AuthorizationStatus: v.authorizationStatus, Status: v.status, ConfirmedAtUtc: v.confirmedAtUtc } : null);
}
